/**
 * Вкладки внутри экрана.
 *
 * design: derived, not documented — сегментированный контрол `.seg` из
 * components.css, в светлом варианте. app-kit.md §6 требует внутри составного
 * виджета работы стрелками, поэтому это настоящий `tablist`, а не ряд кнопок:
 * стрелки двигают выбор, Home и End прыгают к краям, в порядок обхода попадает
 * только выбранная вкладка.
 */

import { useRef, type KeyboardEvent, type ReactNode } from 'react';

export interface Tab<K extends string> {
  key: K;
  title: string;
  /** Число рядом с названием: сколько там строк. Не показывается, если нет. */
  count?: number;
}

export function Tabs<K extends string>({
  label,
  tabs,
  active,
  onChange,
}: {
  label: string;
  tabs: Tab<K>[];
  active: K;
  onChange: (key: K) => void;
}) {
  const strip = useRef<HTMLDivElement>(null);

  function onKey(event: KeyboardEvent<HTMLButtonElement>) {
    const at = tabs.findIndex((tab) => tab.key === active);
    let next = at;
    if (event.key === 'ArrowRight') next = (at + 1) % tabs.length;
    else if (event.key === 'ArrowLeft') next = (at - 1 + tabs.length) % tabs.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = tabs.length - 1;
    else return;

    event.preventDefault();
    const tab = tabs[next];
    if (!tab) return;
    onChange(tab.key);
    strip.current?.querySelectorAll('button')[next]?.focus();
  }

  return (
    // Обработчик стрелок висит на самих вкладках, а не на полосе: полоса роли
    // `tablist` в порядок обхода не попадает, и слушать клавиатуру на ней
    // значит слушать её у элемента, который никогда не получит фокус.
    <div className="seg light" role="tablist" aria-label={label} ref={strip}>
      {tabs.map((tab) => (
        <button
          key={tab.key}
          type="button"
          role="tab"
          id={`tab-${tab.key}`}
          aria-selected={tab.key === active}
          aria-controls={`panel-${tab.key}`}
          tabIndex={tab.key === active ? 0 : -1}
          onKeyDown={onKey}
          onClick={() => onChange(tab.key)}
        >
          {tab.title}
          {tab.count === undefined ? null : (
            <span className="seg-count">{tab.count}</span>
          )}
        </button>
      ))}
    </div>
  );
}

export function TabPanel<K extends string>({
  tab,
  children,
}: {
  tab: K;
  children: ReactNode;
}) {
  return (
    <div role="tabpanel" id={`panel-${tab}`} aria-labelledby={`tab-${tab}`} tabIndex={-1}>
      {children}
    </div>
  );
}
